<template>
  <v-card class="mx-auto" elevation="0">
    <v-sheet height="175px" class="px-3 pt-4 primary lighten-2">
      <v-text-field class="mb-2" v-model="search" label="Search Devices" dark flat solo-inverted hide-details clearable
        clear-icon="mdi-close-circle-outline"></v-text-field>
      <v-select v-model="filterValue" :items="filterItems" item-text="name" attach return-object dark
        label="Filter by severity" multiple>
        <template #selection="{ item }">
          <v-chip small light close @input="removeFilter(item.name)" text-color="grey darken-4"
            :color="`${severityToColor(item.severity)} lighten-2`">{{item.name}}</v-chip>
        </template>
      </v-select>
    </v-sheet>
    <v-card-text style="overflow:auto; height:325px">
      <v-container fill-height justify-center v-if="loading">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-container>
      <v-treeview v-else :items="filteredDeviceTree" :search="search">
        <template v-slot:prepend="{ item }">
          <v-icon :color="`${severityToColor(item.severity)} lighten-2`">bookmark</v-icon>
        </template>
        <template v-slot:label="{ item }">
          <a @click="mapFocus(item)">{{ item.name }}</a>
        </template>
      </v-treeview>

    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'DeviceStatusTree',
  emits: ['mapFocus', 'mapFocusBounds'],
  data () {
    return {
      deviceTree: undefined,
      search: null,
      loading: true,
      filterItems: [
        { name: 'Normal', severity: 0 },
        { name: 'Info', severity: 1 },
        { name: 'Warning', severity: 2 },
        { name: 'Minor', severity: 3 },
        { name: 'Major', severity: 4 },
        { name: 'Critical', severity: 5 }
      ],
      filterValue: [
        { name: 'Warning', severity: 2 },
        { name: 'Minor', severity: 3 },
        { name: 'Major', severity: 4 },
        { name: 'Critical', severity: 5 }
      ]
    }
  },
  computed: {
    filteredDeviceTree () {
      const severityList = this.filterValue.map((x) => x.severity)
      if (Array.isArray(this.deviceTree)) {
        return this.filterTreeByLeaf(this.deviceTree, (x) =>
          severityList.includes(x.severity)
        )
      }
      return this.deviceTree
    }
  },
  methods: {
    /**
     * Remove a specific filter from the list
     *
     * @param {string} name Name of the filter to remove
     */
    removeFilter (name) {
      this.filterValue = this.filterValue.filter((x) => x.name !== name)
    },
    /**
     * Open the popup or zoom to bounds for a given device / group of devices on the map
     *
     * @param {*} item The device item to focus
     */
    mapFocus (item) {
      if (item.type === 'device') {
        this.$emit('mapFocus', item.name)
      } else if (Array.isArray(item.children)) {
        const totalChildren = this.flattenLeaves(item.children).map(
          (item2) => item2.name
        )
        this.$emit('mapFocusBounds', totalChildren)
      }
    },
    /**
     * Flatten all leaves of a tree structure into one array
     *
     * @param {*} tree The tree to flatten
     *
     * @returns {Object[]} Array of items from the tree
     */
    flattenLeaves (tree) {
      const _flatten = (layer) => {
        const flat = []
        for (const item of layer) {
          if (Array.isArray(item.children)) {
            flat.push(..._flatten(item.children))
          } else {
            flat.push(item)
          }
        }
        return flat
      }
      return _flatten(tree)
    },
    /**
     * Filter a Vuetify treeview structure by the content of its leaves.
     *
     * @param {*} tree Vuetify treeview items
     * @param {*} predicate Function used to filter each item
     *
     * @returns {Object[]} Filtered tree items
     */
    filterTreeByLeaf (tree, predicate) {
      const _filter = (layer, predicate) => {
        const newLayer = []
        for (const item of layer) {
          const canHaveChildren = Array.isArray(item.children)
          const hasChildren = item.children.length > 0
          const childrenCanHaveChildren = !Array.isArray(
            item.children[0].children
          )
          const lastLayerWithChildren =
            canHaveChildren && hasChildren && childrenCanHaveChildren
          const children = lastLayerWithChildren
            ? item.children.filter(predicate)
            : _filter(item.children, predicate)
          const severity = Math.max(...children.map((item2) => item2.severity))
          const newItem = { ...item, children, severity }
          if (newItem.children.length > 0) {
            newLayer.push(newItem)
          }
        }
        return newLayer
      }
      return _filter(tree, predicate)
    },
    /**
     * Get the organisations, project groups, and device types and return them in a format compatible with Vuetify treeview.
     *
     * @returns {Object | undefined} Tree view items or undefined if fetch failed
     */
    async fetchZoneTree () {
      try {
        const statusResponse = await this.$http.get('/devicestatus')
        const statuses = statusResponse.data.statuses
        const devicesResponse = await this.$http.get('/schemas/devices')
        const devices = devicesResponse.data.devices

        const devicetypesResponse = await this.$http.get(
          '/schemas/devicetypes'
        )
        const devicetypeids = {}
        devicetypesResponse.data.devicetypes.forEach((item) => {
          devicetypeids[item.typename] = item.id
        })

        // Combine data from both sources
        const deviceStatuses = []
        statuses.forEach((item) => {
          const device = devices.find(
            (device) => device.devicename === item.device.name
          )
          deviceStatuses.push({
            id: `device_${item.id}`,
            name: device.devicename,
            severity: item.severity,
            platformid: devicetypeids[device.typename],
            type: 'device'
          })
        })
        const projectgroupsResponse = await this.$http.get(
          '/schemas/projectgroups'
        )
        const projectgroups = projectgroupsResponse.data.projectgroups.map(
          (item) => {
            const children = deviceStatuses.filter((item2) =>
              item.devicetypes.includes(item2.platformid)
            )
            return {
              id: `projectgroup_${item.id}`,
              platformid: item.id,
              name: item.groupname,
              children,
              type: 'project_group'
            }
          }
        )
        const organisationsResponse = await this.$http.get(
          '/schemas/organisations'
        )
        const organisations = organisationsResponse.data.organisations.map(
          (item) => {
            const children = projectgroups.filter((item2) =>
              item.groups.includes(item2.platformid)
            )
            return {
              id: `organisation_${item.id}`,
              platformid: item.id,
              name: item.companyname,
              children,
              type: 'organisation'
            }
          }
        )

        return organisations
      } catch (error) {
        this.error = 'Error fetching zones'
      }
    },
    /**
     * Convert severity index to Vuetify colour
     *
     * @param {number} severity Severity enum index
     */
    severityToColor (severity) {
      switch (severity) {
        case 1:
          return 'gray'
        case 2:
          return 'blue'
        case 3:
          return 'yellow'
        case 4:
          return 'orange'
        case 5:
          return 'red'
        default:
          return 'green'
      }
    }
  },
  async mounted () {
    this.deviceTree = await this.fetchZoneTree()
    this.loading = false
  }
}
</script>

<style>
</style>
